import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Landmark,
  Loader2,
  TrendingUp,
  Receipt,
} from "lucide-react";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bucketLabels = {
  BC_12:      "BC · 12%",
  HST_13:     "HST 13% (ON/NS/NB/NL/PEI)",
  GST_5:      "GST 5% only",
  QST_14975:  "QC · 14.975%",
  INT_0:      "International · 0%",
  UNKNOWN:    "Unknown / not confirmed",
};

// CRA Reporting Dashboard — admin-only. Reads pre-computed summary from
// /api/admin/cra/summary and lets ops one-click download three CSVs
// (revenue, expenses, taxes) ready for a CRA filing.
const CRADashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/cra/summary?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load CRA summary");
    } finally {
      setLoading(false);
    }
  }, [token, year]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const download = (kind) => {
    const url = `${API}/admin/cra/export.csv?year=${year}&kind=${kind}&auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
    toast.success(`Downloading ${kind}_${year}.csv…`);
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="cra-dashboard-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center" data-testid="cra-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">StardomFans · Admin</span>
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 px-3 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm focus:border-[#EC008C] focus:outline-none"
              data-testid="cra-year-select"
            >
              {[0, 1, 2].map((n) => {
                const y = now.getFullYear() - n;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
            <div className="text-right hidden sm:block">
              <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Compliance</p>
              <p className="text-white font-bold">CRA Reporting</p>
            </div>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* KPI row */}
        {loading || !summary ? (
          <div className="p-10 flex items-center justify-center text-[#8A92A0] gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading summary…</div>
        ) : (
          <>
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Gross revenue" value={`$${summary.total_revenue.toFixed(2)}`} tone="green" icon={TrendingUp} />
              <KPI label="Tax collected" value={`$${summary.total_tax_collected.toFixed(2)}`} tone="amber" icon={Landmark} />
              <KPI label="Total expenses" value={`$${summary.total_expenses.toFixed(2)}`} tone="red" icon={Receipt} />
              <KPI label="Net income" value={`$${summary.net_income.toFixed(2)}`} tone="white" icon={TrendingUp} />
            </section>

            {/* Revenue by bucket */}
            <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#1A2030] flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Revenue by tax bucket</p>
                <button
                  onClick={() => download("taxes")}
                  className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#EC008C] text-black text-xs font-bold hover:bg-[#FF3399]"
                  data-testid="cra-download-taxes"
                >
                  <Download className="w-3 h-3" /> Taxes CSV
                </button>
              </div>
              <table className="w-full text-sm" data-testid="cra-tax-table">
                <thead className="text-[10px] uppercase tracking-widest text-[#8A92A0]">
                  <tr className="border-b border-[#1A2030]">
                    <th className="text-left px-4 py-2 font-medium">Bucket</th>
                    <th className="text-right px-4 py-2 font-medium">Net revenue</th>
                    <th className="text-right px-4 py-2 font-medium">Tax collected</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(summary.revenue_by_bucket).map((b) => (
                    <tr key={b} className="border-b border-[#1A2030]" data-testid={`cra-tax-row-${b}`}>
                      <td className="px-4 py-2 text-white">{bucketLabels[b] || b}</td>
                      <td className="px-4 py-2 text-right text-white">${summary.revenue_by_bucket[b].toFixed(2)}</td>
                      <td className="px-4 py-2 text-right text-[#F0C464]">${(summary.tax_collected_by_bucket[b] || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* Expenses by category */}
            <section className="rounded-xl border border-[#1A2030] bg-[#0A0E18] overflow-hidden">
              <div className="px-4 py-3 border-b border-[#1A2030] flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Expenses by CRA category</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => download("expenses")}
                    className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#1A2030] text-white text-xs font-bold hover:bg-[#252d3f]"
                    data-testid="cra-download-expenses"
                  >
                    <Download className="w-3 h-3" /> Expenses CSV
                  </button>
                  <button
                    onClick={() => download("revenue")}
                    className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-[#1A2030] text-white text-xs font-bold hover:bg-[#252d3f]"
                    data-testid="cra-download-revenue"
                  >
                    <Download className="w-3 h-3" /> Revenue CSV
                  </button>
                </div>
              </div>
              <table className="w-full text-sm" data-testid="cra-expense-table">
                <thead className="text-[10px] uppercase tracking-widest text-[#8A92A0]">
                  <tr className="border-b border-[#1A2030]">
                    <th className="text-left px-4 py-2 font-medium">Category</th>
                    <th className="text-right px-4 py-2 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(summary.expenses_by_category).filter((c) => summary.expenses_by_category[c] !== 0).map((c) => (
                    <tr key={c} className="border-b border-[#1A2030]">
                      <td className="px-4 py-2 text-white">{c}</td>
                      <td className="px-4 py-2 text-right text-white">${summary.expenses_by_category[c].toFixed(2)}</td>
                    </tr>
                  ))}
                  {Object.values(summary.expenses_by_category).every((v) => v === 0) && (
                    <tr><td colSpan="2" className="px-4 py-6 text-center text-[#8A92A0] text-xs">No expenses recorded for {year}.</td></tr>
                  )}
                </tbody>
              </table>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

const KPI = ({ label, value, tone, icon: Icon }) => (
  <div className={`rounded-xl border p-4 ${
    tone === "green" ? "border-[#8DB87A]/30 bg-[#8DB87A]/5" :
    tone === "amber" ? "border-[#F0C464]/30 bg-[#F0C464]/5" :
    tone === "red"   ? "border-[#EC008C]/30 bg-[#EC008C]/5" :
                       "border-[#1A2030] bg-[#0A0E18]"
  }`}>
    <div className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${
        tone === "green" ? "text-[#8DB87A]" :
        tone === "amber" ? "text-[#F0C464]" :
        tone === "red"   ? "text-[#EC008C]" :
                            "text-[#8A92A0]"
      }`} />
      <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">{label}</p>
    </div>
    <p className="text-2xl font-bold text-white mt-1">{value}</p>
  </div>
);

export default CRADashboard;
